import { type ReadonlyNonEmptyArray } from 'fp-ts/ReadonlyNonEmptyArray'

import { type IImage } from '@custom/shared/types'

export interface PlansPageData {
  readonly introSection: {
    readonly title: string
    readonly items: IntroConfig
  }
  readonly plansComparisonSection: {
    readonly scrollToFeatures: string
    readonly plans: PlansConfig
  }
  readonly trustedBySection: {
    readonly heading?: string
    readonly logos?: IImage[]
  }
  readonly testimonialsSection: {
    readonly title: string
    readonly testimonials: TestimonialsConfig
  }
  readonly featuresComparisonSection: {
    readonly title: string
    readonly products: FeaturesConfig
  }
}

export type IntroConfig = {
  [key in Product]: { readonly name: Capitalize<key>; readonly copy: string }
}
export const PlanKinds = ['free', 'pro', 'business', 'enterprise'] as const
export type PlanKind = (typeof PlanKinds)[number]
export type PlansConfig = { [key in PlanKind]: Plan<key> }

export const TestimonialCompanies = ['gonimbly', 'zapier', 'dispel'] as const
export type TestimonialCompany = (typeof TestimonialCompanies)[number]
export type TestimonialsConfig = { [key in TestimonialCompany]: Testimonial }

export const Products = ['mail', 'grammarly', 'coda', 'go'] as const
export type Product = (typeof Products)[number]
export const ProductSections = [...Products, 'shared'] as const
export type ProductSection = (typeof ProductSections)[number]
export type ProductAvailability = { [key in Product]: 'available' | 'unavailable' }
export type ButtonStyleVariant = 'primary' | 'secondary' | 'tertiary' | 'text'
export type FeaturesConfig = { [key in ProductSection]: ReadonlyNonEmptyArray<Feature> }
export type Link = {
  prefix?: string
  label: string
  url: string
}

export type PlanCtaUrlParams = { cid: string }
export type PlanCtaUrl = (p: PlanCtaUrlParams) => string

export interface Plan<Kind extends PlanKind> {
  readonly kind: Kind
  readonly title: Capitalize<Kind>
  readonly targetAudience: string
  readonly description: string
  readonly billing: {
    readonly price: string
    readonly cadence?: string
    readonly monthlyPrice?: {
      readonly price: string
      readonly label: string
    }
  }
  readonly cta: {
    readonly disabledLabel: string
    readonly label: string
    readonly floatingLabel: string
    readonly url: (params: PlanCtaUrlParams) => string
  }
  readonly features: {
    readonly products: ProductAvailability
    readonly included: string
    readonly list: ReadonlyNonEmptyArray<string>
  }
}

export interface Testimonial {
  readonly quote: string
  readonly name: string
  readonly position: string
}

export interface FeatureAvailability {
  readonly name: string | Link
  readonly tooltip?: string
  readonly availability: { [key in PlanKind]: string | ReadonlyNonEmptyArray<string> }
}

export interface Feature {
  readonly productName: string
  readonly sections: ReadonlyNonEmptyArray<{
    readonly name: string
    readonly features: ReadonlyNonEmptyArray<FeatureAvailability>
  }>
}

export const PlansPageFlags = {
  DISABLE_FREE_CTA: 'disableFreePlanCta',
  DISABLE_PRO_CTA: 'disableProPlanCta',
  DISABLE_BUSINESS_CTA: 'disableBusinessPlanCta',
  DISABLE_ENTERPRISE_CTA: 'disableEnterprisePlanCta',
} as const

export type PlansPageFlag = (typeof PlansPageFlags)[keyof typeof PlansPageFlags]
